import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="animated-replies" 
export default class extends Controller {
  static targets = ["form", "submitButton", "textarea"]
  
  connect() {
    this.originalSubmitText = this.submitButtonTarget.textContent
  }

  async submitReply(event) {
    event.preventDefault()
    
    const form = this.formTarget
    const formData = new FormData(form)
    const textarea = this.textareaTarget
    const replyText = textarea.value.trim()
    
    if (!replyText) return
    
    // Show loading state
    this.setLoadingState()
    
    try {
      // Create floating reply effect
      const floatingReply = this.createFloatingReply(replyText)
      
      // Submit form via AJAX
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      
      if (response.ok) {
        const result = await response.json()
        console.log('Reply response:', result)
        
        // Animate the floating reply to its final position
        await this.animateReplyToPosition(floatingReply, result.html)
        
        // Update parent comment reply count if provided
        if (result.parent_id && result.parent_reply_count !== undefined) {
          console.log('Attempting to update parent comment reply count:', result.parent_id, result.parent_reply_count)
          this.updateParentCommentReplyCount(result.parent_id, result.parent_reply_count)
          
          // The updateParentCommentReplyCount method now handles thread view as well
        }
        
        // Update total comments count if provided
        if (result.total_comments_count !== undefined) {
          this.updateTotalCommentsCount(result.total_comments_count)
        }
        
        // Reset form (don't hide it)
        this.resetForm()
        
        // Show success feedback
        this.showSuccessFeedback()
        
        // Dispatch event to notify other controllers about the new reply
        document.dispatchEvent(new CustomEvent('reply-submitted', {
          detail: {
            parentId: result.parent_id,
            replyId: result.reply_id,
            html: result.html
          }
        }))
        
      } else {
        // Handle error
        this.handleError()
        floatingReply.remove()
      }
      
    } catch (error) {
      console.error('Error submitting reply:', error)
      this.handleError()
    }
  }
  
  createFloatingReply(text) {
    // Create a floating preview of the reply
    const floatingDiv = document.createElement('div')
    floatingDiv.className = 'fixed z-50 bg-green-50 dark:bg-night-surface border border-green-300 dark:border-night-border rounded-lg p-3 shadow-xl max-w-sm pointer-events-none'
    floatingDiv.style.transition = 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
    
    // Get form position
    const formRect = this.formTarget.getBoundingClientRect()
    floatingDiv.style.left = `${formRect.left + 10}px`
    floatingDiv.style.top = `${formRect.top}px`
    floatingDiv.style.opacity = '0'
    floatingDiv.style.transform = 'scale(0.9) translateY(-10px)'
    
    // Add content with reply indicator
    floatingDiv.innerHTML = `
      <div class="flex items-start space-x-2">
        <div class="flex-shrink-0">
          <div class="w-6 h-6 rounded-full bg-green-500 dark:bg-night-primary flex items-center justify-center text-white font-bold text-xs animate-pulse">
            ${this.getCurrentUserInitial()}
          </div>
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-xs text-gray-700 dark:text-night-text-primary">${this.escapeHtml(text.substring(0, 80))}${text.length > 80 ? '...' : ''}</div>
          <div class="text-xs text-green-600 dark:text-night-primary mt-1 flex items-center">
            <i class="fas fa-reply mr-1"></i>
            Replying...
          </div>
        </div>
      </div>
    `
    
    document.body.appendChild(floatingDiv)
    
    // Trigger entrance animation
    requestAnimationFrame(() => {
      floatingDiv.style.opacity = '1'
      floatingDiv.style.transform = 'scale(1) translateY(0)'
    })
    
    return floatingDiv
  }
  
  async animateReplyToPosition(floatingReply, newReplyHtml) {
    // Parse the new reply HTML
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = newReplyHtml
    const newReply = tempDiv.firstElementChild
    
    // Add the new reply to the replies list (hidden initially)
    newReply.style.opacity = '0'
    newReply.style.transform = 'translateX(20px)'
    newReply.classList.add('bg-blue-50', 'dark:bg-night-primary/10', 'border-l-4', 'border-blue-400', 'dark:border-night-primary')
    
    // Find where to insert the reply in the new thread system
    this.insertReplyInThread(newReply)
    
    // Get the final position
    const finalRect = newReply.getBoundingClientRect()
    
    // Animate floating reply to final position
    floatingReply.style.left = `${finalRect.left}px`
    floatingReply.style.top = `${finalRect.top}px`
    floatingReply.style.transform = 'scale(0.95)'
    floatingReply.style.opacity = '0.8'
    
    // Wait for animation to complete
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Remove floating reply
    floatingReply.remove()
    
    // Reveal the actual reply with a slide-in effect
    newReply.style.transition = 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
    newReply.style.opacity = '1'
    newReply.style.transform = 'translateX(0)'
    
    // Scroll to the new reply smoothly
    newReply.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    })
    
    // Remove highlight after a moment
    setTimeout(() => {
      newReply.style.transition = 'all 1s ease'
      newReply.classList.remove('bg-blue-50', 'dark:bg-night-primary/10', 'border-l-4', 'border-blue-400', 'dark:border-night-primary')
    }, 2500)
  }
  
  insertReplyInThread(newReply) {
    // Look for the replies list in the thread view
    let repliesContainer = document.querySelector('[data-thread-navigation-target="repliesList"]')
    
    if (repliesContainer) {
      // We're in a thread view - need to wrap the reply in the proper structure
      const replyWrapper = document.createElement('div')
      replyWrapper.className = 'py-6 px-6'
      replyWrapper.setAttribute('data-controller', 'comments')
      replyWrapper.appendChild(newReply)
      
      // Insert at the end of the replies list
      repliesContainer.appendChild(replyWrapper)
      
      // Update the reply count in the thread header
      this.updateReplyCount()
      
      // Check if there was a "no replies" message and remove it
      const noRepliesMessage = repliesContainer.querySelector('.text-center')
      if (noRepliesMessage && noRepliesMessage.textContent.includes('No replies yet')) {
        noRepliesMessage.remove()
      }
      
    } else {
      // Fallback: look for the main comments list and append there
      repliesContainer = document.querySelector('[data-animated-comments-target="commentsList"]')
      if (repliesContainer) {
        repliesContainer.appendChild(newReply)
      } else {
        // Last resort: find any replies container
        repliesContainer = document.querySelector('.divide-y.divide-gray-200')
        if (repliesContainer) {
          repliesContainer.appendChild(newReply)
        }
      }
    }
  }
  
  updateReplyCount() {
    // Update reply count display if we're in a thread view
    const repliesContainer = document.querySelector('[data-thread-navigation-target="repliesList"]')
    if (repliesContainer) {
      // Count actual reply items (excluding "no replies" message)
      const replyItems = repliesContainer.querySelectorAll('.py-6.px-6[data-controller="comments"]')
      const replyCount = replyItems.length
      
      // Update the reply count in the main comment section
      const replyCountElement = document.querySelector('.px-6.py-6.border-b .mt-4 .flex.items-center.space-x-6 .flex.items-center.text-sm.text-gray-500.font-medium span')
      if (replyCountElement && replyCountElement.parentElement.querySelector('.fa-comments')) {
        const replyText = replyCount === 1 ? 'reply' : 'replies'
        replyCountElement.textContent = `${replyCount} ${replyText}`
        
        // Add a subtle highlight animation
        replyCountElement.style.color = '#2563eb'
        replyCountElement.style.transition = 'color 0.5s ease'
        
        setTimeout(() => {
          replyCountElement.style.color = ''
        }, 1000)
      }
    }
  }
  
  setLoadingState() {
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.innerHTML = `
      <i class="fas fa-spinner fa-spin mr-1"></i>
      Replying...
    `
    this.textareaTarget.disabled = true
  }
  
  resetForm() {
    this.textareaTarget.value = ''
    this.textareaTarget.disabled = false
    this.textareaTarget.style.height = '40px' // Reset autogrow to match min-height
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.innerHTML = this.originalSubmitText
    
    // Focus back to textarea for next reply
    setTimeout(() => this.textareaTarget.focus(), 100)
  }
  
  updateParentCommentReplyCount(parentId, newCount) {
    // Try multiple approaches to find and update the parent comment's reply count
    
    // 1. Try to find the main comment view reply button (when not in thread view)
    const parentReplyButton = document.querySelector(`[data-thread-navigation-comment-id-value="${parentId}"]`)
    if (parentReplyButton) {
      const repliesCountSpan = parentReplyButton.querySelector('.replies-count')
      if (repliesCountSpan) {
        const replyText = newCount === 1 ? 'reply' : 'replies'
        repliesCountSpan.textContent = `${newCount} ${replyText}`
        
        // Add highlight animation
        repliesCountSpan.style.background = '#dbeafe'
        repliesCountSpan.style.padding = '2px 6px'
        repliesCountSpan.style.borderRadius = '4px'
        repliesCountSpan.style.transition = 'all 0.3s ease'
        
        setTimeout(() => {
          repliesCountSpan.style.background = 'transparent'
          repliesCountSpan.style.padding = '0'
        }, 1500)
        
        console.log(`Updated parent comment ${parentId} reply count to ${newCount} (main view)`)
        return
      }
    }
    
    // 2. Try to find the thread view reply count (when in thread view)
    const threadReplyCount = document.querySelector('.thread-reply-count')
    if (threadReplyCount) {
      const replyText = newCount === 1 ? 'reply' : 'replies'
      threadReplyCount.textContent = `${newCount} ${replyText}`
      
      // Add highlight animation
      threadReplyCount.style.color = '#2563eb'
      threadReplyCount.style.transition = 'color 0.5s ease'
      
      setTimeout(() => {
        threadReplyCount.style.color = ''
      }, 1000)
      
      console.log(`Updated parent comment ${parentId} reply count to ${newCount} (thread view)`)
      return
    }
    
    // 3. Fallback: try to find by data-comment-id
    const fallbackParent = document.querySelector(`[data-comment-id="${parentId}"]`)
    if (fallbackParent) {
      const repliesCountSpan = fallbackParent.querySelector('.replies-count')
      if (repliesCountSpan) {
        const replyText = newCount === 1 ? 'reply' : 'replies'
        repliesCountSpan.textContent = `${newCount} ${replyText}`
        console.log(`Updated parent comment ${parentId} reply count to ${newCount} (fallback)`)
        return
      }
    }
    
    console.warn(`Could not find parent comment ${parentId} reply count element to update`)
  }
  
  updateCurrentThreadReplyCount(newCount) {
    // Update the reply count displayed in the current thread view header
    const threadReplyCount = document.querySelector('.px-6.py-6.border-b .mt-4 .flex.items-center.space-x-6 .flex.items-center.text-sm .fas.fa-comments')?.parentElement?.querySelector('span')
    if (threadReplyCount) {
      const replyText = newCount === 1 ? 'reply' : 'replies'
      threadReplyCount.textContent = `${newCount} ${replyText}`
      console.log(`Updated current thread reply count to ${newCount}`)
    }
  }
  
  updateTotalCommentsCount(newCount) {
    // Update comment count in post engagement stats
    const postCommentCount = document.querySelector('[data-post-comment-count]')
    if (postCommentCount) {
      postCommentCount.innerHTML = `<i class="fas fa-comment mr-1.5 text-gray-400 dark:text-night-text-muted"></i>${newCount}`
      
      // Add highlight animation
      postCommentCount.style.color = '#2563eb'
      postCommentCount.style.transition = 'color 0.5s ease'
      
      setTimeout(() => {
        postCommentCount.style.color = ''
      }, 1000)
      
      console.log(`Updated post comment count to ${newCount}`)
    } else {
      console.warn('Could not find post comment count element [data-post-comment-count]')
    }
  }
  
  showSuccessFeedback() {
    // Brief success animation (we could add this to the parent comment)
    console.log('Reply posted successfully!')
  }
  
  handleError() {
    this.resetForm()
    
    // Show error state
    const button = this.submitButtonTarget
    const originalBg = button.className
    
    button.className = button.className.replace('bg-blue-600', 'bg-red-500')
    button.innerHTML = `<i class="fas fa-exclamation-triangle mr-1"></i>Error`
    
    setTimeout(() => {
      button.className = originalBg
      button.innerHTML = this.originalSubmitText
    }, 3000)
  }
  
  getCurrentUserInitial() {
    // Extract from existing avatar or default to '?'
    const existingAvatar = document.querySelector('.h-8.w-8.rounded-full .text-white')
    return existingAvatar?.textContent?.trim() || '?'
  }
  
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}